import { useEffect, useState } from "react";
import { Box, Button, Chip, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../../api/crud";
import {
  EQUIPMENT_ATTRIBUTES_ENDPOINT,
  ERROR_SNACKBAR,
  SUCCESS_SNACKBAR,
} from "../../../consts";

interface EquipmentAttributeFormProps {
  id?: string;
  onSuccess?: (data?: any) => void;
}

interface OptionInput {
  id?: number;
  name: string;
}

export default function EquipmentAttributeForm({
  id,
  onSuccess,
}: EquipmentAttributeFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, update, loading } = useCRUD(
    EQUIPMENT_ATTRIBUTES_ENDPOINT
  );

  const isEdit = Boolean(id);
  const [name, setName] = useState("");
  const [choices, setChoices] = useState<OptionInput[]>([]);
  const [optionInputValue, setOptionInputValue] = useState("");

  useEffect(() => {
    if (!isEdit || !id) return;
    get(id).then((res) => {
      if (!res?.data) return;
      setName(res.data.name || "");
      const items = Array.isArray(res.data.choices)
        ? res.data.choices.map((c: { id?: number; name: string }) => ({
            id: c.id,
            name: c.name || "",
          }))
        : [];
      setChoices(items);
    });
  }, [isEdit, id, get]);

  const addOptionFromInput = () => {
    const trimmed = optionInputValue.trim();
    if (!trimmed) return;
    setChoices((prev) => [...prev, { name: trimmed }]);
    setOptionInputValue("");
  };

  const removeOption = (index: number) => {
    setChoices((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOptionInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addOptionFromInput();
    }
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      enqueueSnackbar(t("Name is required"), ERROR_SNACKBAR);
      return;
    }

    const payloadChoices = choices
      .map((o) => (o.id != null ? { id: o.id, name: o.name.trim() } : { name: o.name.trim() }))
      .filter((o) => o.name);

    if (isEdit && id) {
      const result = await update({
        id,
        name: trimmedName,
        choices: payloadChoices,
      });
      if (result?.success) {
        enqueueSnackbar(t("Configuration updated successfully"), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
      } else {
        const msg =
          (typeof result?.errorData === "string"
            ? result.errorData
            : (result?.errorData as { message?: string })?.message) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
      return;
    }

    const result = await create({
      name: trimmedName,
      choices: payloadChoices,
    });
    if (result?.data?.id != null) {
      enqueueSnackbar(t("Configuration created successfully"), SUCCESS_SNACKBAR);
      onSuccess?.(result.data);
    } else {
      const msg =
        (typeof result?.errorData === "string"
          ? result.errorData
          : (result?.errorData as { message?: string })?.message) ||
        t("There was an error, please review the form.");
      enqueueSnackbar(msg, ERROR_SNACKBAR);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        size="small"
        label={t("Name")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
      />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <TextField
            size="small"
            label={t("Choice name")}
            value={optionInputValue}
            onChange={(e) => setOptionInputValue(e.target.value)}
            onKeyDown={handleOptionInputKeyDown}
            placeholder={t("Type and press Enter or Add")}
            fullWidth
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addOptionFromInput}
            sx={{
              flexShrink: 0,
              height: 40,
              minHeight: 40,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            {t("Add")}
          </Button>
        </Box>
        {choices.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {choices.map((opt, index) => (
              <Chip
                key={opt.id != null ? opt.id : `new-${index}`}
                label={opt.name}
                onDelete={() => removeOption(index)}
                size="small"
              />
            ))}
          </Box>
        )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="primary" disabled={loading} onClick={handleSubmit}>
          {loading ? t("Processing...") : t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
